import { ZodType } from 'zod';
import {
  BrowsingScopeLevel,
  ListQueryPersistenceRoute
} from 'v5/core/list-query/listQueryDefinition';
import {
  parseQuerySearch,
  stringifyQueryParams
} from 'v5/core/list-query/queryString';
import { PatternConfig } from './createPersistentBrowserHistory';

export type { BrowsingScopeLevel };

export type ListPersistenceRoute = ListQueryPersistenceRoute;

export type ListPersistenceConfig<T extends object> = {
  listId: string;
  schema: ZodType<T>;
  routes: ListPersistenceRoute[];
};

/**
 * Builds a flat localStorage key from browsing scope + list id (+ optional subtype).
 *
 * Examples:
 * - persistedQueries/org/acme/resources
 * - persistedQueries/org/acme/w/alpha/resources
 * - persistedQueries/org/acme/w/alpha/t/cast/productions/12
 */
export function buildListPersistenceKey(options: {
  listId: string;
  browsingScope: BrowsingScopeLevel;
  routeParams: Record<string, string>;
  subtypeParam?: string;
}): string | null {
  const { listId, browsingScope, routeParams, subtypeParam } = options;
  const organizationId = routeParams.app_id;
  if (!organizationId) {
    return null;
  }

  const segments = ['persistedQueries', 'org', organizationId];

  if (browsingScope === 'workspace' || browsingScope === 'team') {
    const workspaceId = routeParams.workspaceId;
    if (!workspaceId) {
      return null;
    }
    segments.push('w', workspaceId);
  }

  if (browsingScope === 'team') {
    const teamId = routeParams.teamId;
    if (!teamId) {
      return null;
    }
    segments.push('t', teamId);
  }

  segments.push(listId);

  if (subtypeParam) {
    const subtype = routeParams[subtypeParam];
    if (!subtype) {
      return null;
    }
    segments.push(subtype);
  }

  return segments.join('/');
}

/**
 * Expands a declarative list definition into PatternConfig entries for the interceptor.
 */
export function listPersistence<T extends object>(
  config: ListPersistenceConfig<T>
): Array<PatternConfig<T>> {
  return config.routes.map(route => ({
    pattern: route.pattern,
    getPersistenceKey: routeParams =>
      buildListPersistenceKey({
        listId: config.listId,
        browsingScope: route.browsingScope,
        routeParams,
        subtypeParam: route.subtypeParam
      }),
    parseQueryParams: (search: string) =>
      config.schema.parse(parseQuerySearch(search)),
    serializeQueryParams: stringifyQueryParams
  }));
}
